import toast from 'reusable-components/notifications/toast';
import {
  AppUpdateInfo,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
  addFlexibleUpdateListener,
  completeFlexibleUpdate,
  getAppUpdateInfo,
  isUpdateAvailable,
  openAppStore,
  performImmediateUpdate,
  removeAllListeners,
  startFlexibleUpdate,
} from 'utils/AppUpdate';
import Logger from 'utils/Logger';
import { isAndroidApp, isIosApp } from 'utils/platform';
import { create } from 'zustand';

type CheckForUpdateOptions = {
  startWhenAvailable: boolean;
  trigger: 'manual' | 'auto';
};

type InAppUpdateState = {
  updateAvailable: boolean;
  checkingForUpdate: boolean;
  lastAutoCheck: Date | null;
  lastManualCheck: Date | null;
  updateInfo: AppUpdateInfo | null;
  checkForUpdate: (options: CheckForUpdateOptions) => Promise<void>;
  isUpdateCheckAllowed: (trigger: 'manual' | 'auto') => boolean;
};

const handleIosUpdate = async (_info: AppUpdateInfo): Promise<void> => {
  const confirmed = window.confirm(
    'A new version is available. Would you like to update now?',
  );

  if (confirmed) {
    try {
      await openAppStore();
    } catch {
      window.alert('Could not open App Store. Please update manually.');
    }
  }
};

const handleAndroidUpdate = async (info: AppUpdateInfo): Promise<void> => {
  if (!info.flexibleUpdateAllowed) {
    if (info.immediateUpdateAllowed) {
      try {
        const result = await performImmediateUpdate();
        if (result?.code === AppUpdateResultCode.CANCELED) {
          toast({
            text: 'Update canceled. You can update later.',
            icon: 'ℹ️',
            position: 'top-center',
          });
        }
      } catch (error) {
        Logger.error('Error performing immediate update:', error);
      }
      return;
    }

    await openAppStore();
    return;
  }

  try {
    const result = await startFlexibleUpdate();

    if (!result) {
      return;
    }

    switch (result.code) {
      case AppUpdateResultCode.OK:
        toast({
          text: 'Downloading update in the background...',
          icon: '⬇️',
          position: 'top-center',
        });

        await addFlexibleUpdateListener((state) => {
          switch (state.installStatus) {
            case FlexibleUpdateInstallStatus.DOWNLOADED:
              removeAllListeners();
              toast({
                text: 'Update ready! Tap here to install.',
                icon: '✅',
                position: 'top-center',
                variant: 'success',
                onClick: () => {
                  completeFlexibleUpdate();
                },
              });
              break;

            case FlexibleUpdateInstallStatus.FAILED:
              removeAllListeners();
              toast({
                text: 'Update download failed. Please try again later.',
                icon: '❌',
                position: 'top-center',
                variant: 'error',
              });
              break;
          }
        });
        break;

      case AppUpdateResultCode.CANCELED:
        toast({
          text: 'Update canceled. You can update later.',
          icon: 'ℹ️',
          position: 'top-center',
        });
        break;
    }
  } catch {
    toast({
      text: 'Could not start update. Please try again later.',
      icon: '❌',
      position: 'top-center',
      variant: 'error',
    });
  }
};

const initiateUpdate = async (info: AppUpdateInfo): Promise<void> => {
  try {
    if (isIosApp()) {
      await handleIosUpdate(info);
    } else if (isAndroidApp()) {
      await handleAndroidUpdate(info);
    }
  } catch (error) {
    Logger.error('Error initiating update:', error);
  }
};

const MAXIMUM_AUTO_CHECK_INTERVAL = 5 * 60 * 1000;
const MAXIMUM_MANUAL_CHECK_INTERVAL = 3 * 1000;

export const useInAppUpdateStore = create<InAppUpdateState>()((set, get) => ({
  updateAvailable: false,
  checkingForUpdate: false,
  lastAutoCheck: null,
  lastManualCheck: null,
  updateInfo: null,

  isUpdateCheckAllowed: (trigger) => {
    const now = Date.now();
    const interval =
      trigger === 'manual'
        ? MAXIMUM_MANUAL_CHECK_INTERVAL
        : MAXIMUM_AUTO_CHECK_INTERVAL;
    const lastCheck =
      trigger === 'manual' ? get().lastManualCheck : get().lastAutoCheck;

    if (lastCheck && now - lastCheck.getTime() < interval) {
      return false;
    }

    return true;
  },

  checkForUpdate: async (options) => {
    if (!get().isUpdateCheckAllowed(options.trigger)) {
      return;
    }

    const timestampKey =
      options.trigger === 'manual' ? 'lastManualCheck' : 'lastAutoCheck';
    set({ [timestampKey]: new Date(), checkingForUpdate: true });

    try {
      const info = await getAppUpdateInfo();
      const hasUpdate = isUpdateAvailable(info);

      set({ updateInfo: info, updateAvailable: hasUpdate });

      if (hasUpdate && info) {
        if (options.startWhenAvailable) {
          await initiateUpdate(info);
        } else {
          toast({
            text: 'Update available. Tap to update.',
            icon: 'ℹ️',
            position: 'top-center',
            onClick: () => initiateUpdate(info),
          });
        }
      } else if (options.trigger === 'manual') {
        toast({
          text: 'Your app is up to date!',
          icon: '✅',
          position: 'top-center',
          variant: 'success',
        });
      }
    } catch (error) {
      Logger.error('Error checking for update:', error);

      if (options.trigger === 'manual') {
        toast({
          text: 'Could not check for updates. Please try again.',
          icon: '❌',
          position: 'top-center',
          variant: 'error',
        });
      }
    } finally {
      set({ checkingForUpdate: false });
    }
  },
}));
